<template>
  <div id="app">
    <the-heading ref="heading"></the-heading>
    <div class="main">
      <router-view />
    </div>
    <ai-chat-widget></ai-chat-widget>
    <div
      v-if="selectionMenu.visible"
      class="selection-ask-menu"
      :style="{ left: selectionMenu.x + 'px', top: selectionMenu.y + 'px' }"
      @mousedown.prevent
      @contextmenu.prevent
    >
      <div class="selection-ask-title">对选中文本提问</div>
      <div v-if="selectionMenu.loading" class="selection-ask-empty">正在加载问题...</div>
      <template v-else>
        <button
          v-for="item in selectionMenu.questions"
          :key="item"
          type="button"
          class="selection-ask-item"
          @click="askSelectionQuestion(item)"
        >
          {{ item }}
        </button>
        <div v-if="selectionMenu.questions.length === 0" class="selection-ask-empty">暂无可用问题</div>
      </template>
    </div>
    <the-footer></the-footer>
  </div>
</template>

<script>
import TheHeading from '@/components/TheHeading'
import TheFooter from '@/components/TheFooter'
import AiChatWidget from '@/components/AiChatWidget'
import ElementUI from 'element-ui'

export default {
  name: 'App',
  components: {
    TheHeading,
    TheFooter,
    AiChatWidget
  },
  data () {
    return {
      selectionMenu: {
        visible: false,
        loading: false,
        x: 0,
        y: 0,
        selectedText: '',
        questions: []
      },
      selectionRequestSeq: 0,
      inactivityTimer: null, // 无操作定时器
      debounceTimer: null, // 防抖定时器
      timeoutDuration: 30 * 60 * 1000 // 30分钟（毫秒）
    }
  },
  methods: {
    reLogin () {
      console.log('app')
      // this.$refs["heading"].isLogined = false
      this.$refs['heading'].logoutAction()
    },
    normalizeSelectedText () {
      if (!window.getSelection) return ''
      const selection = window.getSelection()
      if (!selection || selection.isCollapsed) return ''
      const text = selection.toString().replace(/\s+/g, ' ').trim()
      if (!text || text.length > 80) return ''
      return text
    },
    getAiChatCategory () {
      const fallback = { categoryType: 'common', categoryValue: 'common' }
      const stored = localStorage.getItem('aiChatCategory')
      if (!stored) return fallback
      try {
        const parsed = JSON.parse(stored)
        if (parsed && parsed.categoryType && parsed.categoryValue) {
          return {
            categoryType: parsed.categoryType,
            categoryValue: String(parsed.categoryValue)
          }
        }
      } catch (e) {
        return fallback
      }
      return fallback
    },
    isInsideSelectionUi (target) {
      if (!target || !target.closest) return false
      return !!target.closest('.selection-ask-menu') || !!target.closest('.ai-chat-widget')
    },
    canShowSelectionAskMenu (target) {
      if (!target || !target.closest) return false
      if (!target.closest('.main')) return false
      return !target.closest('input, textarea, select, button, a, [contenteditable="true"], .el-input, .el-textarea')
    },
    handleGlobalContextMenu (event) {
      if (this.isInsideSelectionUi(event.target)) return
      if (!this.canShowSelectionAskMenu(event.target)) {
        this.hideSelectionMenu()
        return
      }
      const text = this.normalizeSelectedText()
      if (!text) {
        this.hideSelectionMenu()
        return
      }
      event.preventDefault()
      this.showSelectionMenu(event.clientX, event.clientY, text)
    },
    showSelectionMenu (clientX, clientY, text) {
      const x = Math.max(8, Math.min(clientX, window.innerWidth - 340))
      const y = Math.max(8, Math.min(clientY, window.innerHeight - 220))
      this.selectionMenu = {
        visible: true,
        loading: true,
        x,
        y,
        selectedText: text,
        questions: []
      }
      this.loadSelectionQuestions(text)
    },
    loadSelectionQuestions (text) {
      const requestSeq = ++this.selectionRequestSeq
      const category = this.getAiChatCategory()
      const params = new URLSearchParams()
      params.append('limit', 3)
      params.append('categoryType', category.categoryType || 'common')
      params.append('categoryValue', category.categoryValue || 'common')
      this.$http.get('/retrieve/aiChat/getSelectionPresetQuestions?' + params.toString(), { timeout: 30000 })
        .then(res => {
          if (requestSeq !== this.selectionRequestSeq) return
          if (!res || !res.data || res.data.status !== 200) {
            this.hideSelectionMenu()
            return
          }
          const list = (res.data.data && res.data.data.list) || []
          const questions = list.map(item => {
            return item && item.content ? String(item.content).split('【】').join(text) : ''
          }).filter(Boolean).slice(0, 3)
          if (questions.length === 0) {
            this.hideSelectionMenu()
            return
          }
          this.selectionMenu.loading = false
          this.selectionMenu.questions = questions
        })
        .catch(() => {
          if (requestSeq === this.selectionRequestSeq) this.hideSelectionMenu()
        })
    },
    askSelectionQuestion (question) {
      this.hideSelectionMenu()
      if (window.getSelection) {
        window.getSelection().removeAllRanges()
      }
      if (this.$bus) {
        this.$bus.$emit('ai-chat-ask', question)
      }
    },
    hideSelectionMenu () {
      this.selectionRequestSeq++
      this.selectionMenu.visible = false
      this.selectionMenu.loading = false
      this.selectionMenu.questions = []
    },
    handleGlobalClick (event) {
      if (!this.selectionMenu.visible || this.isInsideSelectionUi(event.target)) return
      this.hideSelectionMenu()
    },
    handleGlobalKeydown (event) {
      if (event.key === 'Escape') {
        this.hideSelectionMenu()
      }
    },
    // 启动30分钟无操作超时检测
    startInactivityTimer () {
      // 清除现有定时器
      this.clearInactivityTimer()

      // 检查用户是否已登录
      const isLogined = localStorage.getItem('isLogined') === 'true'
      if (!isLogined) {
        return // 如果未登录，不需要设置定时器
      }

      // 设置新的定时器
      this.inactivityTimer = setTimeout(() => {
        this.handleSessionTimeout()
      }, this.timeoutDuration)
    },
    // 清除超时定时器
    clearInactivityTimer () {
      if (this.inactivityTimer) {
        clearTimeout(this.inactivityTimer)
        this.inactivityTimer = null
      }
    },
    // 重置无操作定时器（用户活动时调用）
    resetInactivityTimer () {
      // 立即清除现有定时器
      this.clearInactivityTimer()

      // 检查用户是否已登录
      const isLogined = localStorage.getItem('isLogined') === 'true'
      if (!isLogined) {
        return // 如果未登录，不需要设置定时器
      }

      // 使用防抖，避免频繁触发
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer)
      }
      this.debounceTimer = setTimeout(() => {
        this.startInactivityTimer()
      }, 500) // 500毫秒内的多次活动只触发一次重置
    },
    // 处理会话超时
    handleSessionTimeout () {
      // 检查用户是否仍在登录状态
      const isLogined = localStorage.getItem('isLogined') === 'true'
      if (!isLogined) return
      // 机构(IP)免登录用户优先尝试自动登录, 失败才退回退出登录
      const heading = this.$refs.heading
      if (heading && heading.handleTokenExpired) {
        heading.handleTokenExpired('30分钟未操作，自动退出登录。如需继续访问，请重新登录。')
      } else {
        // 显示登录过期提示
        ElementUI.Message.warning({
          message: '30分钟未操作，自动退出登录。如需继续访问，请重新登录。',
          duration: 5000
        })

        // 执行退出登录操作
        this.reLogin()
      }
    },
    // 用户活动事件处理
    handleUserActivity () {
      this.resetInactivityTimer()
    }
  },
  mounted () {
    document.addEventListener('contextmenu', this.handleGlobalContextMenu)
    document.addEventListener('click', this.handleGlobalClick)
    document.addEventListener('keydown', this.handleGlobalKeydown)
    this.$http.get('/static/data/words.csv').then(response => {
      const words = response.data.split('\n')
      let obj = {}
      for (let i = 1; i < words.length - 1; i++) {
        const values = words[i].split(',')
        obj[values[1]] = {}
        obj[values[1]]['same'] = values[2].split('；').filter(s => {
          return s.trim().length > 0
        })
        obj[values[1]]['similar'] = values[3].split('；').filter(s => {
          return s.length > 0
        })
        obj[values[1]]['up'] = values[4].split('；').filter(s => {
          return s.length > 0
        })
        obj[values[1]]['down'] = values[5].split('；').filter(s => {
          return s.length > 0
        })
        obj[values[1]]['ref'] = values[6].split('；').filter(s => {
          return s.length > 0
        })
        obj[values[1]]['modern'] = values[7].split('；').filter(s => {
          return s.trim().length > 0
        })
      }
      this.$global.wordList = obj
    })

    // 监听用户活动事件
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click', 'keydown', 'wheel']
    events.forEach(event => {
      document.addEventListener(event, this.handleUserActivity, true)
    })

    // 如果页面加载时已登录，启动定时器
    const isLogined = localStorage.getItem('isLogined') === 'true'
    if (isLogined) {
      this.startInactivityTimer()
    }
  },
  beforeDestroy () {
    // 清除无操作定时器
    this.clearInactivityTimer()
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }

    // 移除用户活动监听
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click', 'keydown', 'wheel']
    events.forEach(event => {
      document.removeEventListener(event, this.handleUserActivity, true)
    })

    document.removeEventListener('contextmenu', this.handleGlobalContextMenu)
    document.removeEventListener('click', this.handleGlobalClick)
    document.removeEventListener('keydown', this.handleGlobalKeydown)
  }
}
</script>

<style>
#app {
  font-family: myhei, "Avenir", Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
  font-size: 18px;
  min-width: 1300px;
}

body {
  margin: 0;
  padding: 0;
}

.main {
  background-image: url(assets/images/background.png);
  background-size: 2112px;
  background-position-x: center;
  background-position-y: -250px;
  background-repeat: no-repeat;
  background-color: #f7efe7;
  padding-top: 38px;
  min-height: 700px;
}

.selection-ask-menu {
  position: fixed;
  z-index: 3300;
  width: 320px;
  padding: 10px;
  box-sizing: border-box;
  background: #fffaf5;
  border: 1px solid #d8bea3;
  box-shadow: 0 12px 30px rgba(99, 76, 55, 0.26);
  text-align: left;
}

.selection-ask-title {
  margin-bottom: 7px;
  color: #634c37;
  font-family: mysong, serif;
  font-size: 15px;
  font-weight: bold;
}

.selection-ask-item {
  display: block;
  width: 100%;
  margin-top: 6px;
  padding: 8px 9px;
  border: 1px solid #e1cbb6;
  background: rgba(255, 255, 255, 0.86);
  color: #3c2e23;
  cursor: pointer;
  font-family: myhei, "Avenir", Helvetica, Arial, sans-serif;
  font-size: 13px;
  line-height: 1.5;
  text-align: left;
}

.selection-ask-item:hover {
  border-color: #9e5c1d;
  color: #9e5c1d;
  background: #fff;
}

.selection-ask-empty {
  padding: 8px 0 2px;
  color: #9b826d;
  font-size: 13px;
  line-height: 1.6;
}

button {
  font-family: myhei, "Avenir", Helvetica, Arial, sans-serif;
  font-size: 18px;
}

@font-face {
  font-family: myhei;
  src: url(assets/fonts/SourceHanSansCN-Regular.ttf);
}

@font-face {
  font-family: myhei;
  src: url(assets/fonts/SourceHanSansCN-Bold.ttf);
  font-weight: bold;
}

@font-face {
  font-family: mysong;
  src: url(assets/fonts/SourceHanSerifCN-Regular.ttf);
}

@font-face {
  font-family: mysong;
  src: url(assets/fonts/SourceHanSerifCN-Bold.ttf);
  font-weight: bold;
}

@font-face {
  font-family: mysong;
  src: url(assets/fonts/SourceHanSerifCN-Heavy.otf);
  font-weight: heavy;
}

.el-tooltip__popper.is-light {
  background: #ffffff !important;
  box-shadow: 0px 3px 6px 1px rgba(0, 0, 0, 0.16) !important;
  border-radius: 0px 0px 0px 0px !important;
  opacity: 1;
  border: 1px solid #f7f6f5 !important;
}

.el-tooltip__popper[x-placement^="bottom"] .popper__arrow:after {
  background: url("./assets/images/bookIcons/389.png");
  border: 0;
}

.el-select-dropdown__item.selected {
  color: #634c37 !important;
}

.el-select-dropdown__item.hover {
  color: #9e5c1d;
  background-color: #ecded1;
  border-color: #9e5c1d;
}

.el-cascader .el-input .el-input__inner:focus,
.el-cascader .el-input.is-focus .el-input__inner {
  border-color: #9e5c1d;
}

.el-cascader-node.in-active-path,
.el-cascader-node.is-active,
.el-cascader-node.is-selectable.in-checked-path {
  color: #9e5c1d;
}

.el-radio__input.is-checked .el-radio__inner {
  border-color: #9e5c1d;
  background-color: #9e5c1d;
}

.el-radio__inner:hover {
  border-color: #9e5c1d;
}

em {
  color: red !important;
  font-style: normal;
}
</style>



// WEBPACK FOOTER //
// src/App.vue